import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import store from '@/store'
import {
  AUDIT_IGNORED_FIELDS,
  humanizeFieldName,
  getMeaningfulChanges,
  isIgnoredField
} from '@/constants/audit_constants'

export const useStoryAuditsStore = defineStore('storyAudits', {
  state: () => ({
    audits: [],
    storyId: null,
    loading: false,
    revisionPreview: null,
    revisionVersion: null,
    showModal: false,
    reverting: false,
    untrackedAssociations: []
  }),

  getters: {
    all: ({ audits }) => audits,
    byVersion: ({ audits }) => (version) => {
      // Return all audits for this version (for transaction grouping)
      const versionAudits = audits.filter(a => a.version === version)
      // For backwards compatibility, if only one audit, return it directly
      // If multiple audits in transaction, return the "primary" one:
      // - Prefer attachment audits (create/destroy) over story audits
      // - Otherwise return the first audit
      if (versionAudits.length === 1) return versionAudits[0]
      const attachmentAudit = versionAudits.find(a => a.scope === 'attachment')
      return attachmentAudit || versionAudits[0]
    },
    hasAudits: ({ audits }) => audits.length > 0,

    // Re-export from constants for components that access via store
    ignoredFields: () => AUDIT_IGNORED_FIELDS,

    // Format untracked associations as a human-readable list
    formattedUntrackedAssociations({ untrackedAssociations }) {
      const items = untrackedAssociations
      if (items.length === 0) return ''
      if (items.length === 1) return items[0]
      return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1]
    },

    // Filter audits to hide only lock/unlock operations
    // Transaction grouping handles most other filtering needs
    meaningfulAudits() {
      return this.audits.filter(audit => {
        // Always show audits with comments (revert operations, etc.)
        if (audit.comment && audit.comment.trim()) return true
        
        // Always show attachment audits
        if (audit.scope === 'attachment') return true
        
        // Always show create and destroy actions
        if (audit.action !== 'update') return true
        
        // Filter out standalone lock/unlock operations
        if (audit.audited_changes) {
          const changedFields = Object.keys(audit.audited_changes)
          const onlyLockFields = changedFields.every(f => 
            ['locker_id', 'lock_version', 'locked_at'].includes(f)
          )
          if (onlyLockFields) return false
        }
        
        // Show everything else (transaction grouping handles the rest)
        return true
      })
    },

    // Get unique versions with representative audit for each transaction
    // Priority: 1) Audit with revert comment, 2) Attachment audit, 3) First audit
    uniqueVersions() {
      const meaningful = this.meaningfulAudits
      const versionMap = new Map()
      
      meaningful.forEach(audit => {
        const existing = versionMap.get(audit.version)
        
        if (!existing) {
          // First audit for this version
          versionMap.set(audit.version, audit)
        } else {
          // Prioritize audits with revert comments (they explain the transaction)
          const auditHasRevertComment = audit.comment && audit.comment.includes('Reverted to version')
          const existingHasRevertComment = existing.comment && existing.comment.includes('Reverted to version')
          
          if (auditHasRevertComment && !existingHasRevertComment) {
            // Prefer audit with revert comment
            versionMap.set(audit.version, audit)
          } else if (!auditHasRevertComment && !existingHasRevertComment && audit.scope === 'attachment' && existing.scope !== 'attachment') {
            // If neither has revert comment, prefer attachment audit
            versionMap.set(audit.version, audit)
          }
          // Otherwise keep existing (including when both have revert comments)
        }
      })
      
      // Return as array, maintaining reverse chronological order
      return Array.from(versionMap.values())
    },

    // Latest version from meaningful audits only (ignore filtered-out audits like locker_id changes)
    latestVersion() {
      const meaningful = this.meaningfulAudits
      return meaningful.length > 0 ? Math.max(...meaningful.map(a => a.version)) : 0
    }
  },

  actions: {
    reset() {
      this.audits = []
      this.storyId = null
      this.loading = false
      this.revisionPreview = null
      this.revisionVersion = null
      this.showModal = false
      this.reverting = false
      this.untrackedAssociations = []
    },

    openModal(storyId) {
      this.storyId = storyId
      this.showModal = true
      this.fetchAudits(storyId)
    },

    closeModal() {
      this.showModal = false
      this.revisionPreview = null
      this.revisionVersion = null
    },

    async fetchAudits(storyId) {
      this.storyId = storyId
      this.loading = true

      try {
        const response = await httpClient.get(`/api/internal/stories/${storyId}/audits`)
        this.audits = response.data.audits
        this.untrackedAssociations = response.data.untracked_associations || []
      } catch (err) {
        store.dispatch('messages/smartAdd', err)
      } finally {
        this.loading = false
      }
    },

    async fetchRevision(version) {
      if (!this.storyId) return

      this.loading = true
      this.revisionVersion = version

      try {
        const response = await httpClient.get(`/api/internal/stories/${this.storyId}/revision`, {
          params: { version }
        })
        this.revisionPreview = response.data.story
      } catch (err) {
        store.dispatch('messages/smartAdd', err)
        this.revisionPreview = null
      } finally {
        this.loading = false
      }
    },

    clearRevisionPreview() {
      this.revisionPreview = null
      this.revisionVersion = null
    },

    async revertToVersion(version) {
      if (!this.storyId) return

      this.reverting = true

      try {
        const response = await httpClient.post(`/api/internal/stories/${this.storyId}/revert`, { version })
        store.dispatch('messages/addNotice', `Successfully reverted to version ${version}`)
        // Refresh audits to show the new revert entry
        // Awaited to ensure audit list is updated before returning
        await this.fetchAudits(this.storyId)
        this.clearRevisionPreview()
        return response.data.story
      } catch (err) {
        store.dispatch('messages/smartAdd', err)
        throw err
      } finally {
        this.reverting = false
      }
    },

    // Format changed fields for display
    formatChanges(auditedChanges) {
      if (!auditedChanges) return []

      return Object.entries(auditedChanges).map(([field, change]) => {
        // For 'create' action, change is just the new value
        // For 'update' action, change is [old_value, new_value]
        const isArray = Array.isArray(change)
        return {
          field: humanizeFieldName(field),
          oldValue: isArray ? change[0] : null,
          newValue: isArray ? change[1] : change,
          hasChange: isArray
        }
      })
    },

    // Re-export utility functions for components that access via store
    humanizeFieldName,
    getMeaningfulChanges
  }
})
